import type { StatusRateioUc } from './statusRateioUc';
import { valorDevido } from './statusBoleto.ts';
import type { UnidadeConsumidora } from '../../types';

// `statusDistrato` e `agregarFaturasDaUc` são lógica pura que precisa de
// teste; imports estáticos sem extensão (o alias é do Vite, não do Node) fazem
// `node --test` quebrar ao carregar o módulo inteiro. `import type` acima é
// erasable (some em runtime); `supabase`, `ucCodigoKeys` e `getStatusRateioUc`
// são valores de verdade usados só dentro de função de I/O, então entram via
// import dinâmico bem em cima de onde são usados. Mesma restrição documentada
// em src/lib/confirmacaoPagamentoManual.ts.

export type MotivoCancelamento =
  // Valores antigos: 163 pedidos gravados usam eles.
  | 'inadimplencia' | 'insatisfacao' | 'mudanca_endereco' | 'financeiro' | 'outro'
  // Ampliação de 26/09/2026, medida nos 101 pedidos que estavam como 'outro'.
  | 'placas_proprias' | 'outra_empresa' | 'desconto_baixo'
  | 'imovel_vendido' | 'unidade_desativada' | 'empresa_encerrada';
export type StatusSolicitacaoCancelamento = 'aberta' | 'em_andamento' | 'concluida' | 'cancelada';
export type TipoAlertaCancelamento = 'faturas_em_aberto' | 'saldo_excedente';
export type StatusDistratoResumo = 'sem_distrato' | 'ativo' | 'cancelado';
export type DistratoExterno = 'sim' | 'nao' | 'aguardando';
export type OrigemSolicitacao = 'sistema' | 'importacao' | 'portal' | 'whatsapp';

export const MOTIVO_LABEL: Record<MotivoCancelamento, string> = {
  placas_proprias: 'Instalou placas solares próprias',
  outra_empresa: 'Trocou por outra empresa',
  desconto_baixo: 'Desconto menor que o esperado',
  mudanca_endereco: 'Mudança de endereço',
  imovel_vendido: 'Vendeu o imóvel ou trocou o titular',
  unidade_desativada: 'Unidade desativada',
  empresa_encerrada: 'Empresa encerrou as atividades',
  inadimplencia: 'Inadimplência',
  insatisfacao: 'Insatisfação',
  financeiro: 'Financeiro (cliente)',
  outro: 'Outro',
};

export const STATUS_SOLICITACAO_LABEL: Record<StatusSolicitacaoCancelamento, string> = {
  aberta: 'Aberta',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada (desistência)',
};

interface UcEmbed {
  id: number;
  numero_uc: string;
  outro_numero_uc: string | null;
  situacao: UnidadeConsumidora['situacao'];
  consumo_medio_kwh: number | null;
  cadastro_aprovado: boolean | null;
  rateio_motivo_travamento: 'excesso_saldo' | 'manual' | null;
  rateio_unidade_geradora_id: string | null;
  unidade_geradora: string | null;
  negociacao_id: number | null;
  dados_tecnicos_equatorial: any;
}

export interface TermoEmbed {
  id: number;
  numero_termo: string | null;
  cancelado_em: string | null;
}

export interface SolicitacaoCancelamento {
  id: number;
  uc_id: number;
  uc_numero: string;
  cliente_nome: string;
  cpf_cnpj_cliente: string | null;
  data_solicitacao: string;
  motivo_categoria: MotivoCancelamento;
  motivo_detalhe: string | null;
  vendedor_responsavel_id: string | null;
  status: StatusSolicitacaoCancelamento;
  termo_cancelamento_log_id: number | null;
  prazo_saida_rateio_estimado: string | null;
  prazo_uso_saldo_estimado: string | null;
  concluida_em: string | null;
  concluida_motivo: string | null;
  created_at: string;
  // Campos que nasceram com a importação da planilha histórica
  // (migration 20260908120000). Ver os COMMENT ON COLUMN de lá.
  multaValor: number | null;
  multaSituacao: string | null;
  distratoExterno: DistratoExterno | null;
  distratoExternoData: string | null;
  distratoExternoRef: string | null;
  saldoKwhInformado: number | null;
  debitosAbertosValor: number | null;
  dataRetiradaRateio: string | null;
  observacao: string | null;
  origem: OrigemSolicitacao;
  importLoteId: string | null;
  importOrigemPlanilha: string | null;
  situacaoDivergente: boolean;
  // Etapa "Validar cancelamento" (migration 20260909120000): carimbo de quem
  // passou a solicitação pela conferência antes de o distrato ir para assinatura.
  validadoEm: string | null;
  validadoPor: string | null;
  validacaoJustificativa: string | null;
  // Protocolo e prazo de retorno (#5827, migration 20260916190000). Gerados
  // pelo banco no INSERT; null nas importadas antes do protocolo existir.
  protocolo: string | null;
  prazoRetornoDiasUteis: number | null;
  dataRetornoPrevista: string | null;
  /** Itens congelados na validação — de onde sai a análise financeira (#5829/#5831). */
  validacaoItens: unknown;
  // Retorno da análise enviado ao cliente (#5831).
  respostaEnviadaEm: string | null;
  respostaCanal: string | null;
  respostaTexto: string | null;
  /** Resposta dada à mão (#6127): por onde — whatsapp, email, telefone, presencial. */
  respostaMeio: string | null;
  /** Retorno do cliente depois da resposta (#6127). Null = ainda não respondeu. */
  clienteRetornoEm: string | null;
  clienteRetornoTipo: string | null;
  clienteRetornoObs: string | null;
  // Quem pediu o cancelamento (#5828). Null nos pedidos anteriores ao campo.
  solicitanteNome: string | null;
  solicitanteCpf: string | null;
  solicitanteEmail: string | null;
  solicitanteTelefone: string | null;
  solicitanteVinculo: string | null;
  declaracaoVeracidadeEm: string | null;
}

export interface SolicitacaoCancelamentoDetalhada extends SolicitacaoCancelamento {
  ucSituacao: string | null;
  rateioStatus: StatusRateioUc;
  vendedorNome: string | null;
  saldoAcumuladoKwh: number | null;
  faturasEmAbertoQtd: number;
  faturasEmAbertoValor: number;
  distratoStatus: StatusDistratoResumo;
  distratoNumeroTermo: string | null;
  /**
   * O distrato mostrado veio da UC, não da solicitação (#6128): foi gerado pelo
   * "Gerenciar UC" e nunca ligado a este pedido. Só exibição — nada é gravado.
   */
  distratoDaUc: boolean;
  /**
   * Quando o distrato da UC foi emitido. A tela compara com a data do pedido JÁ
   * corrigida pela aba da planilha (#6122) — comparar com a gravada acusava
   * "anterior ao pedido" em registro cuja data do pedido é que estava errada.
   */
  distratoDaUcEmitidoEm: string | null;
}

export interface CancelamentoAlerta {
  id: string;
  tipo: TipoAlertaCancelamento;
  uc_id: number;
  uc_numero: string | null;
  cliente_nome: string | null;
  data_referencia: string;
  detalhe: Record<string, number>;
  status: 'pendente' | 'tratado' | 'dispensado';
  created_at: string;
}

/** Faturas com boleto em aberto/vencido — mesmo filtro usado na régua de cobrança. */
const STATUS_FATURA_ABERTA = ['Em aberto', 'Vencido'];

export interface FaturaParaAgregacao {
  /** Resultado DO MÊS (excedente − crédito) — não é saldo. Ver `agregarFaturasDaUc`. */
  saldo_acumulado_kwh?: number | null;
  /** "SALDO KWH" impresso na conta da distribuidora: o estoque de créditos da UC. */
  saldo_ugs_vigor?: number | string | null;
  status_boleto_vigor: string;
  valor_boleto_vigor: number | null;
  /** Boleto único / empresa parceira: o valor da cobrança mora aqui e `valor_boleto_vigor` fica 0. */
  valor_emissao_boleto_unico?: number | null;
  status_fatura: string | null;
}

export interface AgregadoFaturasUc {
  saldoAcumuladoKwh: number | null;
  qtdAberto: number;
  valorAberto: number;
}

/**
 * Agrega, para as faturas de UMA UC, o saldo de energia mais recente e o total
 * em aberto — dado o array já buscado do Supabase (pura, sem I/O).
 *
 * - `saldoAcumuladoKwh` vem da PRIMEIRA fatura da lista após o filtro de
 *   excluídas: a chamadora é responsável por passar o array já ordenado por
 *   `created_at` desc (é assim que `listarSolicitacoes` busca); esta função
 *   não reordena.
 * - ⚠️ O saldo é `saldo_ugs_vigor`, o "SALDO KWH" da conta — o estoque de
 *   créditos. NÃO é `saldo_acumulado_kwh`, que apesar do nome é o resultado do
 *   mês (excedente recebido − crédito recebido, ver mapeador-faturas) e chega a
 *   ser negativo. Até a solicitação 6126 o cancelamento usava esse: a UC
 *   386036701225 aparecia com 342 kWh e a conta de SET/2026 dizia 2.446,29 kWh;
 *   em 48 das UCs com cancelamento o número divergia da conta. O resto do sistema
 *   (rateio, cobertura de saldo, Vick) já lia o estoque.
 *   Estoque não informado (null) vira null — "—" na tela —, nunca o número do mês.
 * - Faturas com `status_fatura === 'Excluído'` (registro fantasma do robô de
 *   extração) não contam nem para o saldo nem para o total em aberto.
 * - "Em aberto" usa `STATUS_FATURA_ABERTA`, o mesmo filtro da régua de
 *   cobrança — não confundir com `status_fatura` (etapa do pipeline).
 * - Só conta fatura com valor a cobrar (#6122). Mês sem cobrança fica com
 *   `status_boleto_vigor = 'Em aberto'` e valor 0 ou nulo, sem boleto nenhum — e
 *   a tela de Cancelamentos mostrava "3 · R$ 0,00" em vermelho, como dívida, num
 *   cliente que não devia nada. O valor segue `valorDevido` (boleto único /
 *   parceira primeiro), a mesma régua do painel financeiro.
 */
export function agregarFaturasDaUc(faturas: FaturaParaAgregacao[]): AgregadoFaturasUc {
  const validas = faturas.filter(f => (f.status_fatura || '') !== 'Excluído');
  const estoque = validas.length > 0 ? validas[0].saldo_ugs_vigor : null;
  const saldoAcumuladoKwh = estoque === null || estoque === undefined || estoque === '' || !Number.isFinite(Number(estoque))
    ? null : Number(estoque);
  const emAberto = validas
    .filter(f => STATUS_FATURA_ABERTA.includes(f.status_boleto_vigor))
    .map(f => valorDevido(f as Parameters<typeof valorDevido>[0]))
    .filter(v => v > 0);
  return {
    saldoAcumuladoKwh,
    qtdAberto: emAberto.length,
    valorAberto: emAberto.reduce((s, v) => s + v, 0),
  };
}

async function enriquecerComFaturas(ucs: UcEmbed[]) {
  const { ucCodigoKeys } = await import('./ucCodigo');
  const chaves = Array.from(new Set(ucs.flatMap(u => ucCodigoKeys(u.numero_uc, u.outro_numero_uc))));
  const porUc = new Map<number, { saldoAcumuladoKwh: number | null; qtdAberto: number; valorAberto: number }>();
  if (chaves.length === 0) return porUc;

  const { supabase } = await import('./supabase');

  // PAGINAÇÃO OBRIGATÓRIA: o PostgREST corta a resposta em 1.000 linhas, sem erro
  // e sem aviso. Com muitas UCs de uma vez isso estourava, e como a ordem é
  // `created_at DESC` sobravam só as faturas mais recentes — as UCs do fim da
  // ordenação ficavam SEM nenhuma linha. O efeito não era só "faltar dado":
  // `agregarFaturasDaUc` lê `validas[0].saldo_acumulado_kwh` e conta as em aberto,
  // então essas UCs apareciam com saldo nulo e ZERO faturas em aberto — saudáveis
  // no alerta de risco de cancelamento, justamente o oposto da verdade.
  const PAGINA = 1000;
  const linhas: unknown[] = [];
  let offset = 0;
  for (;;) {
    const { data: pagina, error } = await supabase
      .from('faturas')
      .select('uc_codigo_norm, saldo_ugs_vigor, status_boleto_vigor, valor_boleto_vigor, valor_emissao_boleto_unico, status_fatura, created_at')
      .in('uc_codigo_norm', chaves)
      .order('created_at', { ascending: false })
      // Desempate estável: `created_at` sozinho repete entre faturas do mesmo
      // lote de importação, e sem critério secundário a paginação pode repetir
      // ou pular linhas.
      .order('id', { ascending: false })
      .range(offset, offset + PAGINA - 1);
    if (error) throw error;
    linhas.push(...(pagina || []));
    if (!pagina || pagina.length < PAGINA) break;
    offset += PAGINA;
  }
  const data = linhas;

  const faturas = (data || []) as Array<{
    uc_codigo_norm: string | null;
    saldo_ugs_vigor: number | null;
    status_boleto_vigor: string;
    valor_boleto_vigor: number | null;
    valor_emissao_boleto_unico: number | null;
    status_fatura: string | null;
    created_at: string;
  }>;
  const porChave = new Map<string, typeof faturas>();
  faturas.forEach(f => {
    if (!f.uc_codigo_norm) return;
    if (!porChave.has(f.uc_codigo_norm)) porChave.set(f.uc_codigo_norm, []);
    porChave.get(f.uc_codigo_norm)!.push(f);
  });

  ucs.forEach(uc => {
    const faturasDaUc = ucCodigoKeys(uc.numero_uc, uc.outro_numero_uc).flatMap(k => porChave.get(k) || []);
    porUc.set(uc.id, agregarFaturasDaUc(faturasDaUc));
  });
  return porUc;
}

async function enriquecerComVendedores(ids: string[]) {
  const unicos = Array.from(new Set(ids.filter(Boolean)));
  const nomePorId = new Map<string, string>();
  if (unicos.length === 0) return nomePorId;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.from('profiles').select('id, full_name').in('id', unicos);
  if (error) throw error;
  (data || []).forEach(p => nomePorId.set(p.id, p.full_name));
  return nomePorId;
}

export function statusDistrato(termo: TermoEmbed | null): StatusDistratoResumo {
  if (!termo) return 'sem_distrato';
  return termo.cancelado_em ? 'cancelado' : 'ativo';
}

export interface TermoDaUc {
  id: number;
  uc_id: number;
  numero_termo: string | null;
  cancelado_em: string | null;
  created_at: string;
}

/**
 * Distrato ativo da UC para uma solicitação SEM distrato ligado (#6128).
 *
 * O vínculo solicitação ↔ distrato só é gravado quando o distrato nasce da
 * própria solicitação. Gerado pelo "Gerenciar UC", ele fica só na UC e a tela
 * dizia "Sem distrato gerado" com o distrato ativo — 12 solicitações importadas
 * estavam assim em 28/09/2026. Pega o ativo mais recente; cancelado não conta.
 */
export function distratoAtivoDaUc(
  termos: TermoDaUc[],
  dataSolicitacao: string,
): { termo: TermoDaUc; anteriorAoPedido: boolean } | null {
  const ativos = termos
    .filter(t => !t.cancelado_em)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  if (ativos.length === 0) return null;
  const termo = ativos[0];
  return { termo, anteriorAoPedido: String(termo.created_at).slice(0, 10) < String(dataSolicitacao).slice(0, 10) };
}

export async function listarSolicitacoes(): Promise<SolicitacaoCancelamentoDetalhada[]> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('solicitacoes_cancelamento')
    .select(`
      *,
      unidades_consumidoras ( id, numero_uc, outro_numero_uc, situacao, consumo_medio_kwh, cadastro_aprovado,
        rateio_motivo_travamento, rateio_unidade_geradora_id, unidade_geradora, negociacao_id, dados_tecnicos_equatorial ),
      termo_cancelamento_logs ( id, numero_termo, cancelado_em )
    `)
    .order('data_solicitacao', { ascending: false });
  if (error) throw error;

  const { getStatusRateioUc } = await import('./statusRateioUc');
  const rows = (data || []) as any[];
  const ucs: UcEmbed[] = rows.map(r => r.unidades_consumidoras).filter(Boolean);
  // Distratos da UC para as solicitações sem distrato ligado (#6128). Falha aqui
  // não derruba a lista: a solicitação só volta a mostrar "Sem distrato gerado".
  const ucsSemDistrato = Array.from(new Set(rows.filter(r => !r.termo_cancelamento_logs && r.uc_id).map(r => r.uc_id)));
  const [faturasPorUc, nomesPorVendedor, termosDaUc] = await Promise.all([
    enriquecerComFaturas(ucs),
    enriquecerComVendedores(rows.map(r => r.vendedor_responsavel_id)),
    ucsSemDistrato.length
      ? supabase.from('termo_cancelamento_logs').select('id, uc_id, numero_termo, cancelado_em, created_at').in('uc_id', ucsSemDistrato)
          .then(({ data: t, error: e }) => {
            if (e) { console.error('Erro ao buscar distratos das UCs', e); return [] as TermoDaUc[]; }
            return (t || []) as TermoDaUc[];
          })
      : Promise.resolve([] as TermoDaUc[]),
  ]);
  const termosPorUc = new Map<number, TermoDaUc[]>();
  termosDaUc.forEach(t => {
    if (!termosPorUc.has(t.uc_id)) termosPorUc.set(t.uc_id, []);
    termosPorUc.get(t.uc_id)!.push(t);
  });

  return rows.map((r): SolicitacaoCancelamentoDetalhada => {
    const uc: UcEmbed | null = r.unidades_consumidoras;
    const termoLigado: TermoEmbed | null = r.termo_cancelamento_logs;
    const daUc = !termoLigado ? distratoAtivoDaUc(termosPorUc.get(r.uc_id) || [], r.data_solicitacao) : null;
    const termo: TermoEmbed | null = termoLigado ?? (daUc ? daUc.termo : null);
    const faturas = uc ? faturasPorUc.get(uc.id) : undefined;
    return {
      id: r.id,
      uc_id: r.uc_id,
      uc_numero: r.uc_numero,
      cliente_nome: r.cliente_nome,
      cpf_cnpj_cliente: r.cpf_cnpj_cliente,
      data_solicitacao: r.data_solicitacao,
      motivo_categoria: r.motivo_categoria,
      motivo_detalhe: r.motivo_detalhe,
      vendedor_responsavel_id: r.vendedor_responsavel_id,
      status: r.status,
      termo_cancelamento_log_id: r.termo_cancelamento_log_id,
      prazo_saida_rateio_estimado: r.prazo_saida_rateio_estimado,
      prazo_uso_saldo_estimado: r.prazo_uso_saldo_estimado,
      concluida_em: r.concluida_em,
      concluida_motivo: r.concluida_motivo,
      created_at: r.created_at,
      ucSituacao: uc?.situacao ?? null,
      rateioStatus: uc ? getStatusRateioUc(uc as any) : 'inativa',
      vendedorNome: r.vendedor_responsavel_id ? nomesPorVendedor.get(r.vendedor_responsavel_id) || null : null,
      saldoAcumuladoKwh: faturas?.saldoAcumuladoKwh ?? null,
      faturasEmAbertoQtd: faturas?.qtdAberto ?? 0,
      faturasEmAbertoValor: faturas?.valorAberto ?? 0,
      distratoStatus: statusDistrato(termo),
      distratoNumeroTermo: termo?.numero_termo ?? null,
      distratoDaUc: !!daUc,
      distratoDaUcEmitidoEm: daUc?.termo.created_at ?? null,
      multaValor: r.multa_valor === null || r.multa_valor === undefined ? null : Number(r.multa_valor),
      multaSituacao: r.multa_situacao ?? null,
      distratoExterno: r.distrato_externo ?? null,
      distratoExternoData: r.distrato_externo_data ?? null,
      distratoExternoRef: r.distrato_externo_ref ?? null,
      saldoKwhInformado: r.saldo_kwh_informado === null || r.saldo_kwh_informado === undefined ? null : Number(r.saldo_kwh_informado),
      debitosAbertosValor: r.debitos_abertos_valor === null || r.debitos_abertos_valor === undefined ? null : Number(r.debitos_abertos_valor),
      dataRetiradaRateio: r.data_retirada_rateio ?? null,
      observacao: r.observacao ?? null,
      origem: (r.origem as OrigemSolicitacao) ?? 'sistema',
      importLoteId: r.import_lote_id ?? null,
      importOrigemPlanilha: r.import_origem_planilha ?? null,
      situacaoDivergente: r.situacao_divergente === true,
      validadoEm: r.validado_em ?? null,
      validadoPor: r.validado_por ?? null,
      validacaoJustificativa: r.validacao_justificativa ?? null,
      protocolo: r.protocolo ?? null,
      validacaoItens: r.validacao_itens ?? null,
      respostaEnviadaEm: r.resposta_enviada_em ?? null,
      respostaCanal: r.resposta_canal ?? null,
      respostaTexto: r.resposta_texto ?? null,
      respostaMeio: r.resposta_meio ?? null,
      clienteRetornoEm: r.cliente_retorno_em ?? null,
      clienteRetornoTipo: r.cliente_retorno_tipo ?? null,
      clienteRetornoObs: r.cliente_retorno_obs ?? null,
      solicitanteNome: r.solicitante_nome ?? null,
      solicitanteCpf: r.solicitante_cpf ?? null,
      solicitanteEmail: r.solicitante_email ?? null,
      solicitanteTelefone: r.solicitante_telefone ?? null,
      solicitanteVinculo: r.solicitante_vinculo ?? null,
      declaracaoVeracidadeEm: r.declaracao_veracidade_em ?? null,
      prazoRetornoDiasUteis: r.prazo_retorno_dias_uteis ?? null,
      dataRetornoPrevista: r.data_retorno_prevista ?? null,
    };
  });
}

// ===========================================================================
// Busca livre — nome do cliente, número da UC, CPF/CNPJ, vendedor, motivo.
// ===========================================================================

/**
 * Texto pronto para comparar: sem acento, minúsculo e sem espaço sobrando.
 * "João" e "joao" precisam casar — quem digita na pressa não põe acento.
 */
export function normalizarBusca(valor: string | null | undefined): string {
  return String(valor ?? '')
    .normalize('NFD').replace(/\p{M}/gu, '')
    .toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Casa o termo com qualquer um dos campos. Termo só de dígitos (CPF, CNPJ,
 * número de UC) compara também contra a versão só-dígitos dos campos — assim
 * "11122233344" acha o cliente gravado como "111.222.333-44" e "760000116"
 * acha a UC escrita como "760.000.116".
 */
export function combinaComBusca(termo: string, campos: Array<string | null | undefined>): boolean {
  const alvo = normalizarBusca(termo);
  if (!alvo) return true;
  const textos = campos.map(normalizarBusca).filter(Boolean);
  if (textos.some(t => t.includes(alvo))) return true;
  const digitos = alvo.replace(/\D/g, '');
  if (!digitos || digitos.length !== alvo.length) return false;
  return textos.some(t => t.replace(/\D/g, '').includes(digitos));
}

export function filtrarSolicitacoes<T extends {
  cliente_nome: string; uc_numero: string; cpf_cnpj_cliente: string | null;
  motivo_detalhe: string | null; vendedorNome?: string | null; distratoNumeroTermo?: string | null;
  observacao?: string | null; protocolo?: string | null;
}>(lista: T[], termo: string): T[] {
  if (!normalizarBusca(termo)) return lista;
  return lista.filter(s => combinaComBusca(termo, [
    s.cliente_nome, s.uc_numero, s.cpf_cnpj_cliente, s.motivo_detalhe,
    s.vendedorNome, s.distratoNumeroTermo, s.observacao, s.protocolo,
  ]));
}

// ===========================================================================
// Alertas automáticos: agrupamento por caso + cruzamento com o que já foi
// cancelado. Ver `resumirAlertas` para o porquê.
// ===========================================================================

/** Por que o alerta não precisa mais de tratamento. */
export type ResolucaoAlerta = 'uc_cancelada' | 'cancelamento_concluido';

export const RESOLUCAO_ALERTA_LABEL: Record<ResolucaoAlerta, string> = {
  uc_cancelada: 'UC já cancelada',
  cancelamento_concluido: 'Cancelamento já concluído',
};

export interface AlertaResumido extends CancelamentoAlerta {
  /** Todos os ids do mesmo caso (uc + tipo) — o "Tratar" resolve o caso inteiro. */
  idsDoCaso: string[];
  /** Quantos dias o cron repetiu este mesmo caso. */
  repeticoes: number;
  primeiraOcorrencia: string;
  /** null = ainda precisa ser tratado. */
  resolucao: ResolucaoAlerta | null;
  /** Solicitação de cancelamento desta UC, quando existe (a mais recente). */
  solicitacaoVinculada: { id: number; status: StatusSolicitacaoCancelamento } | null;
}

const PESO_STATUS_SOLICITACAO: Record<StatusSolicitacaoCancelamento, number> = {
  em_andamento: 4, aberta: 3, concluida: 2, cancelada: 1,
};

/**
 * Transforma a enxurrada de alertas na lista de CASOS que sobraram para tratar.
 *
 * Dois problemas resolvidos aqui, os dois relatados pelo financeiro:
 *
 * 1. REPETIÇÃO. `cancelamento_alertas` tem UNIQUE (uc_id, tipo, data_referencia):
 *    o cron roda todo dia e grava uma linha NOVA para o mesmo caso enquanto ele
 *    não é tratado. Em 09/09/2026 eram 698 linhas pendentes para 117 casos reais
 *    — o mesmo cliente aparecia seis vezes na tela. Aqui fica só a ocorrência
 *    mais recente de cada (uc, tipo), com a contagem de repetições ao lado.
 *
 * 2. CASO JÁ RESOLVIDO. O cron só cria alerta para UC ativa, mas nunca volta
 *    para fechar o que criou: quando a UC é cancelada depois, o alerta antigo
 *    fica pendente para sempre. Eram 91 linhas de UCs já canceladas. Cruzar com
 *    `unidades_consumidoras.situacao` e com as solicitações concluídas marca
 *    esses casos como resolvidos, para a tela poder escondê-los.
 *
 * Pura de propósito — recebe o que a chamadora já buscou. A ordenação de saída
 * é por data de referência desc, com os já resolvidos por último.
 */
export function resumirAlertas(
  alertas: CancelamentoAlerta[],
  solicitacoes: Array<{ id: number; uc_id: number; status: StatusSolicitacaoCancelamento }>,
  situacaoPorUcId: Map<number, string | null>,
): AlertaResumido[] {
  const solicitacaoPorUc = new Map<number, { id: number; status: StatusSolicitacaoCancelamento }>();
  const temConcluida = new Set<number>();
  solicitacoes.forEach(s => {
    if (s.status === 'concluida') temConcluida.add(s.uc_id);
    const atual = solicitacaoPorUc.get(s.uc_id);
    // A mais relevante ganha: em andamento > aberta > concluída > desistência.
    // Empate no peso, o id maior (mais recente) vence.
    if (!atual
      || PESO_STATUS_SOLICITACAO[s.status] > PESO_STATUS_SOLICITACAO[atual.status]
      || (PESO_STATUS_SOLICITACAO[s.status] === PESO_STATUS_SOLICITACAO[atual.status] && s.id > atual.id)) {
      solicitacaoPorUc.set(s.uc_id, { id: s.id, status: s.status });
    }
  });

  const porCaso = new Map<string, CancelamentoAlerta[]>();
  alertas.forEach(a => {
    const chave = `${a.uc_id}|${a.tipo}`;
    if (!porCaso.has(chave)) porCaso.set(chave, []);
    porCaso.get(chave)!.push(a);
  });

  const resumidos = Array.from(porCaso.values()).map((doCaso): AlertaResumido => {
    // `data_referencia` é DATE (mesmo dia = empate); `created_at` desempata.
    const ordenados = [...doCaso].sort((a, b) =>
      b.data_referencia.localeCompare(a.data_referencia) || b.created_at.localeCompare(a.created_at));
    const maisRecente = ordenados[0];
    const situacao = situacaoPorUcId.get(maisRecente.uc_id) ?? null;
    const resolucao: ResolucaoAlerta | null =
      situacao === 'Cancelada' ? 'uc_cancelada'
        : temConcluida.has(maisRecente.uc_id) ? 'cancelamento_concluido'
          : null;
    return {
      ...maisRecente,
      idsDoCaso: ordenados.map(a => a.id),
      repeticoes: ordenados.length,
      primeiraOcorrencia: ordenados[ordenados.length - 1].data_referencia,
      resolucao,
      solicitacaoVinculada: solicitacaoPorUc.get(maisRecente.uc_id) ?? null,
    };
  });

  return resumidos.sort((a, b) =>
    Number(!!a.resolucao) - Number(!!b.resolucao)
    || b.data_referencia.localeCompare(a.data_referencia));
}

export function filtrarAlertas(lista: AlertaResumido[], termo: string): AlertaResumido[] {
  if (!normalizarBusca(termo)) return lista;
  return lista.filter(a => combinaComBusca(termo, [a.cliente_nome, a.uc_numero]));
}

export async function listarAlertas(tipo?: TipoAlertaCancelamento, status: string = 'pendente'): Promise<CancelamentoAlerta[]> {
  const { supabase } = await import('./supabase');

  // PAGINAÇÃO OBRIGATÓRIA: o PostgREST corta em 1.000 linhas sem erro e sem
  // aviso. Os dois crons gravam uma linha por UC/tipo TODO DIA enquanto o caso
  // não é tratado — em 09/09/2026 já eram 698 pendentes crescendo ~50/dia.
  // Sem paginar, ao passar de 1.000 os alertas mais ANTIGOS (justamente os
  // casos arrastados) sumiriam da tela sem ninguém perceber.
  const PAGINA = 1000;
  const linhas: CancelamentoAlerta[] = [];
  let offset = 0;
  for (;;) {
    let query = supabase.from('cancelamento_alertas').select('*')
      .order('created_at', { ascending: false })
      // Desempate estável: os alertas de um mesmo cron nascem no mesmo instante,
      // e sem critério secundário a paginação pode repetir ou pular linhas.
      .order('id', { ascending: false })
      .range(offset, offset + PAGINA - 1);
    if (tipo) query = query.eq('tipo', tipo);
    if (status) query = query.eq('status', status);
    const { data, error } = await query;
    if (error) throw error;
    linhas.push(...((data || []) as CancelamentoAlerta[]));
    if (!data || data.length < PAGINA) break;
    offset += PAGINA;
  }
  return linhas;
}

/** Situação atual das UCs citadas nos alertas — entrada de `resumirAlertas`. */
export async function situacoesDasUcs(ucIds: number[]): Promise<Map<number, string | null>> {
  const unicos = Array.from(new Set(ucIds.filter(Boolean)));
  const porId = new Map<number, string | null>();
  if (unicos.length === 0) return porId;
  const { supabase } = await import('./supabase');
  // `.in()` com muitos ids vira uma URL gigante; 300 por vez segura o tamanho.
  const LOTE = 300;
  for (let i = 0; i < unicos.length; i += LOTE) {
    const { data, error } = await supabase
      .from('unidades_consumidoras').select('id, situacao').in('id', unicos.slice(i, i + LOTE));
    if (error) throw error;
    (data || []).forEach((u: any) => porId.set(u.id, u.situacao ?? null));
  }
  return porId;
}

/** Marca vários alertas do mesmo caso de uma vez (o cron repetiu por dias). */
export async function tratarAlertasEmLote(ids: string[], userId: string | undefined): Promise<number> {
  if (ids.length === 0) return 0;
  const { supabase } = await import('./supabase');
  const LOTE = 300;
  let total = 0;
  for (let i = 0; i < ids.length; i += LOTE) {
    const fatia = ids.slice(i, i + LOTE);
    const { error } = await supabase
      .from('cancelamento_alertas')
      .update({ status: 'tratado', tratado_por: userId || null, tratado_em: new Date().toISOString() })
      .in('id', fatia);
    if (error) throw error;
    total += fatia.length;
  }
  return total;
}

export async function tratarAlerta(id: string, userId: string | undefined): Promise<void> {
  const { supabase } = await import('./supabase');
  const { error } = await supabase
    .from('cancelamento_alertas')
    .update({ status: 'tratado', tratado_por: userId || null, tratado_em: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function dispensarAlerta(id: string, motivo: string, userId: string | undefined): Promise<void> {
  const { supabase } = await import('./supabase');
  const { error } = await supabase
    .from('cancelamento_alertas')
    .update({ status: 'dispensado', dispensado_motivo: motivo, tratado_por: userId || null, tratado_em: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Dispensa todas as repetições do mesmo caso de uma vez. */
export async function dispensarAlertasEmLote(ids: string[], motivo: string, userId: string | undefined): Promise<number> {
  if (ids.length === 0) return 0;
  const { supabase } = await import('./supabase');
  const LOTE = 300;
  let total = 0;
  for (let i = 0; i < ids.length; i += LOTE) {
    const fatia = ids.slice(i, i + LOTE);
    const { error } = await supabase
      .from('cancelamento_alertas')
      .update({ status: 'dispensado', dispensado_motivo: motivo, tratado_por: userId || null, tratado_em: new Date().toISOString() })
      .in('id', fatia);
    if (error) throw error;
    total += fatia.length;
  }
  return total;
}

export interface NovaSolicitacaoInput {
  uc: UnidadeConsumidora;
  dataSolicitacao: string;
  motivoCategoria: MotivoCancelamento;
  motivoDetalhe: string;
  vendedorResponsavelId: string | null;
  prazoSaidaRateioEstimado: string | null;
  prazoUsoSaldoEstimado: string | null;
  createdBy: string | undefined;
}

/** Vendedor responsável padrão: negociação que originou a UC. Editável depois na tela. */
export async function buscarResponsavelPorNegociacao(negociacaoId: number | null | undefined): Promise<string | null> {
  if (!negociacaoId) return null;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.from('negociacoes').select('responsavel_id').eq('id', negociacaoId).maybeSingle();
  if (error) throw error;
  return data?.responsavel_id ?? null;
}

/** Linha crua devolvida pelo INSERT: o protocolo e a data de retorno vêm do trigger. */
export interface SolicitacaoCriada {
  id: number;
  protocolo: string | null;
  data_retorno_prevista: string | null;
}

export async function criarSolicitacao(input: NovaSolicitacaoInput): Promise<SolicitacaoCriada> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('solicitacoes_cancelamento')
    .insert({
      uc_id: input.uc.id,
      uc_numero: input.uc.numero_uc,
      cliente_nome: input.uc.nome_cliente,
      cpf_cnpj_cliente: input.uc.cpf_cnpj_cliente || null,
      data_solicitacao: input.dataSolicitacao,
      motivo_categoria: input.motivoCategoria,
      motivo_detalhe: input.motivoDetalhe.trim() || null,
      vendedor_responsavel_id: input.vendedorResponsavelId,
      prazo_saida_rateio_estimado: input.prazoSaidaRateioEstimado || null,
      prazo_uso_saldo_estimado: input.prazoUsoSaldoEstimado || null,
      created_by: input.createdBy || null,
    })
    .select('id, protocolo, data_retorno_prevista')
    .single();
  if (error) throw error;
  return data as SolicitacaoCriada;
}

export async function atualizarStatusSolicitacao(
  id: number,
  status: StatusSolicitacaoCancelamento,
  concluidaMotivo?: string
): Promise<void> {
  const patch: Record<string, any> = { status };
  if (status === 'concluida' || status === 'cancelada') {
    patch.concluida_em = new Date().toISOString();
    patch.concluida_motivo = concluidaMotivo?.trim() || null;
  }
  const { supabase } = await import('./supabase');
  const { error } = await supabase.from('solicitacoes_cancelamento').update(patch).eq('id', id);
  if (error) throw error;
}

/** Carimba o elo quando um distrato é gerado a partir de uma solicitação já aberta. */
export async function vincularTermoGerado(solicitacaoId: number, termoCancelamentoLogId: number): Promise<void> {
  const { supabase } = await import('./supabase');
  const { error } = await supabase
    .from('solicitacoes_cancelamento')
    .update({ termo_cancelamento_log_id: termoCancelamentoLogId, status: 'em_andamento' })
    .eq('id', solicitacaoId);
  if (error) throw error;
}

export interface SolicitacaoHistoricoItem {
  id: number;
  status_anterior: StatusSolicitacaoCancelamento | null;
  status_novo: StatusSolicitacaoCancelamento;
  observacao: string | null;
  alterado_por: string | null;
  alterado_por_nome: string | null;
  alterado_em: string;
}

/** Linha do tempo da solicitação — gravada automaticamente por trigger (ver migration 20260827130000). */
export async function listarHistoricoSolicitacao(solicitacaoId: number): Promise<SolicitacaoHistoricoItem[]> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('solicitacoes_cancelamento_historico')
    .select('id, status_anterior, status_novo, observacao, alterado_por, alterado_em')
    .eq('solicitacao_id', solicitacaoId)
    .order('alterado_em', { ascending: true });
  if (error) throw error;

  const rows = (data || []) as any[];
  const ids = Array.from(new Set(rows.map(r => r.alterado_por).filter(Boolean)));
  const nomePorId = new Map<string, string>();
  if (ids.length > 0) {
    const { data: profiles } = await supabase.from('profiles').select('id, full_name').in('id', ids);
    (profiles || []).forEach((p: any) => nomePorId.set(p.id, p.full_name));
  }

  return rows.map(r => ({
    ...r,
    alterado_por_nome: r.alterado_por ? nomePorId.get(r.alterado_por) || null : null,
  }));
}

/**
 * Carimba o retorno enviado ao cliente (#5831) pela RPC SECURITY DEFINER da
 * migration 20260923170000 — o histórico da solicitação nunca é escrito pelo
 * client. `canal` fica 'manual' enquanto a demanda 06 (envio automático) não existe.
 */
export async function registrarRespostaCancelamento(
  solicitacaoId: number,
  texto: string,
  canal: 'manual' | 'email' | 'whatsapp' | 'email_whatsapp' = 'manual',
  /** Só na resposta dada por fora (canal 'manual'): quando e por onde foi (#6127). */
  porFora?: { enviadaEm: string; meio: MeioRespostaManual },
): Promise<void> {
  const { supabase } = await import('./supabase');
  const { error } = await supabase.rpc('registrar_resposta_cancelamento', {
    p_solicitacao_id: solicitacaoId,
    p_texto: texto,
    p_canal: canal,
    p_enviada_em: porFora?.enviadaEm ?? null,
    p_meio: porFora?.meio ?? null,
  });
  if (error) throw error;
}

export type MeioRespostaManual = 'whatsapp' | 'email' | 'telefone' | 'presencial';
export const MEIO_RESPOSTA_LABEL: Record<MeioRespostaManual, string> = {
  whatsapp: 'WhatsApp', email: 'E-mail', telefone: 'Ligação', presencial: 'Presencial',
};

export type TipoRetornoCliente = 'confirmou' | 'desistiu' | 'negociar' | 'outro';

/** Registra o que o cliente respondeu ao retorno da análise (#6127). Não muda o status. */
export async function registrarRetornoClienteCancelamento(
  solicitacaoId: number,
  tipo: TipoRetornoCliente,
  retornoEm: string,
  obs?: string,
): Promise<void> {
  const { supabase } = await import('./supabase');
  const { error } = await supabase.rpc('registrar_retorno_cliente_cancelamento', {
    p_solicitacao_id: solicitacaoId,
    p_tipo: tipo,
    p_retorno_em: retornoEm,
    p_obs: obs?.trim() || null,
  });
  if (error) throw error;
}
